<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[9998] bg-slate-950/70 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
    @click.self="$emit('close')"
  >
    <div
      class="bg-white dark:bg-[#111C30] w-full max-w-5xl max-h-[92vh] rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-[#263449] relative animate-fade-in flex flex-col shadow-2xl overflow-hidden"
    >
      <!-- Modal Loading Overlay -->
      <AppLoader
        :isVisible="isLoading"
        variant="card"
        size="md"
        message="Loading Student Details..."
        subtext="Fetching enrollment, contacts, and performance metrics"
        color="indigo"
      />

      <!-- Top Subtle Gradient Accent Line -->
      <div class="h-1 bg-gradient-to-r from-indigo-600 via-blue-600 to-sky-500 shrink-0"></div>

      <!-- Professional Modal Header Bar -->
      <div
        class="bg-white dark:bg-[#111C30] px-6 py-4 border-b border-slate-100 dark:border-[#263449] flex items-center justify-between shrink-0"
      >
        <div class="flex items-center gap-3">
          <div>
            <div class="flex items-center gap-2.5 flex-wrap">
              <h2 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
                Student Profile
              </h2>
              <span
                v-if="student?.rollNumber"
                class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/50"
              >
                Roll: {{ student.rollNumber }}
              </span>
              <span
                class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold capitalize border"
                :class="
                  student?.status === 'active'
                    ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-500/30'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                "
              >
                <span
                  class="w-1.5 h-1.5 rounded-full"
                  :class="student?.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'"
                ></span>
                {{ student?.status || 'Active' }}
              </span>
            </div>
            <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-normal">
              {{ typeof student?.class === 'object' ? student?.class?.name : (student?.class || 'Enrolled') }} • {{ student?.email || 'Student Record' }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            @click="$emit('promote', student)"
            class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300 hover:text-indigo-800 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 border border-indigo-200 dark:border-indigo-800 rounded-xl transition-all cursor-pointer"
          >
            <GraduationCap class="w-3.5 h-3.5" />
            <span>Promote</span>
          </button>
          <button
            @click="$emit('edit', student)"
            class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 bg-slate-50 dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#1f2d47] border border-slate-200/80 dark:border-[#263449] rounded-xl transition-all"
          >
            <Pencil class="w-3.5 h-3.5" />
            <span>Edit Profile</span>
          </button>
          <button
            @click="$emit('close')"
            :disabled="isLoading"
            class="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors disabled:opacity-50"
            title="Close Modal"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Scrollable Modal Content -->
      <div
        v-if="student"
        class="overflow-y-auto p-5 sm:p-7 space-y-6 flex-1 bg-slate-50/50 dark:bg-[#0B1120]"
      >
        <!-- Hero Identity Card -->
        <div
          class="relative bg-white dark:bg-[#111C30] rounded-2xl p-5 sm:p-6 border border-slate-200/80 dark:border-[#263449] shadow-xs flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6"
        >
          <!-- Avatar with status indicator -->
          <div class="relative shrink-0">
            <div class="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden ring-4 ring-slate-100 dark:ring-slate-800 shadow-md">
              <img
                v-if="student.photo"
                :src="student.photo"
                :alt="student.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-600 text-white flex items-center justify-center text-2xl font-bold tracking-wider"
              >
                {{ getInitials(student.name || "") }}
              </div>
            </div>
            <span
              class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full ring-2 ring-white dark:ring-[#111C30]"
              :class="
                student.status === 'active'
                  ? 'bg-emerald-500'
                  : student.status === 'passout'
                  ? 'bg-blue-500'
                  : 'bg-rose-500'
              "
              :title="student.status"
            ></span>
          </div>

          <!-- Hero Details -->
          <div class="flex-1 text-center sm:text-left min-w-0 w-full">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 class="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                  {{ student.name || "Student Name" }}
                </h3>
                <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2">
                  <!-- Class badge -->
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/60 shadow-2xs"
                  >
                    <GraduationCap class="w-3.5 h-3.5 text-indigo-500" />
                    <span>
                      Class:
                      {{
                        typeof student.class === "object"
                          ? student.class?.name
                          : student.class || "N/A"
                      }}
                    </span>
                  </span>

                  <!-- Status capsule -->
                  <span
                    v-if="student.status === 'active'"
                    class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/60 shadow-2xs"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    Active Student
                  </span>
                  <span
                    v-else-if="student.status === 'passout'"
                    class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/60 shadow-2xs"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                    Graduated
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900/60 shadow-2xs"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                    Inactive
                  </span>
                </div>
              </div>

              <!-- Quick Action / Edit -->
              <div class="mt-2 sm:mt-0 flex items-center justify-center sm:justify-end gap-2">
                <button
                  @click="$emit('edit', student)"
                  class="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-xs transition"
                >
                  <Pencil class="w-3.5 h-3.5" />
                  <span>Edit Profile</span>
                </button>
              </div>
            </div>

            <!-- Attendance & Card ID Bar -->
            <div class="mt-3.5 pt-3 border-t border-slate-100 dark:border-[#1E293B] flex items-center justify-center sm:justify-start gap-4 text-xs text-slate-500 dark:text-slate-400 font-mono">
              <span class="flex items-center gap-1.5">
                <IdCard class="w-3.5 h-3.5 text-slate-400" />
                <span>Attendance ID:</span>
                <strong class="text-slate-700 dark:text-slate-200">{{ student.attendanceID || "—" }}</strong>
              </span>
              <span v-if="student.enrollmentDate" class="flex items-center gap-1.5">
                <Calendar class="w-3.5 h-3.5 text-slate-400" />
                <span>Admitted:</span>
                <strong class="text-slate-700 dark:text-slate-200">{{ formatDate(student.enrollmentDate) }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- Navigation Section Switcher -->
        <div class="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-[#18243A] rounded-2xl border border-slate-200/60 dark:border-[#263449]">
          <button
            type="button"
            @click="activeSection = 'profile'"
            :class="[
              'flex-1 py-2 px-3 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2',
              activeSection === 'profile'
                ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs'
                : 'text-slate-500 dark:text-[#7E8DA5] hover:text-slate-800 dark:hover:text-white',
            ]"
          >
            <User class="w-3.5 h-3.5" />
            <span>Profile & Docs</span>
          </button>

          <button
            type="button"
            @click="activeSection = 'fees'"
            :class="[
              'flex-1 py-2 px-3 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2',
              activeSection === 'fees'
                ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs'
                : 'text-slate-500 dark:text-[#7E8DA5] hover:text-slate-800 dark:hover:text-white',
            ]"
          >
            <Wallet class="w-3.5 h-3.5" />
            <span>Fees & Ledger</span>
            <span
              v-if="(feeData?.lifetimeSummary?.grandTotalOutstanding || 0) > 0"
              class="w-2 h-2 rounded-full bg-rose-500"
            ></span>
          </button>

          <button
            type="button"
            @click="activeSection = 'attendance'"
            :class="[
              'flex-1 py-2 px-3 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2',
              activeSection === 'attendance'
                ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs'
                : 'text-slate-500 dark:text-[#7E8DA5] hover:text-slate-800 dark:hover:text-white',
            ]"
          >
            <Calendar class="w-3.5 h-3.5" />
            <span>Attendance Graph</span>
          </button>
        </div>

        <!-- SECTION 1: PROFILE & ACADEMIC INFO -->
        <div v-if="activeSection === 'profile'" class="space-y-6">
        <!-- 2-Column Info Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <!-- Card 1: Personal & Contact Information -->
          <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <User class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Contact & Personal Info</span>
            </div>

            <div class="space-y-3 text-xs">
              <!-- Email -->
              <div class="flex items-start gap-3">
                <Mail class="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Email Address</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium truncate mt-0.5">{{ student.email || "—" }}</p>
                </div>
              </div>

              <!-- Primary Phone -->
              <div class="flex items-start gap-3">
                <Phone class="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Primary Phone</p>
                  <p class="text-slate-800 dark:text-slate-200 font-mono font-medium mt-0.5">{{ student.phone || "—" }}</p>
                </div>
              </div>

              <!-- Secondary Phone -->
              <div class="flex items-start gap-3">
                <Phone class="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Guardian Phone</p>
                  <p class="text-slate-800 dark:text-slate-200 font-mono font-medium mt-0.5">{{ student.secondaryPhone || "—" }}</p>
                </div>
              </div>

              <!-- DOB -->
              <div class="flex items-start gap-3">
                <Calendar class="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Date of Birth</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    {{ student.dob ? formatDate(student.dob) : "—" }}
                    <span v-if="student.dob" class="text-indigo-600 dark:text-indigo-400 font-bold ml-1.5">
                      ({{ calculateAge(student.dob) }} yrs)
                    </span>
                  </p>
                </div>
              </div>

              <!-- Address Details -->
              <div class="flex items-start gap-3">
                <MapPin class="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Location & Address</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    {{ [student.city, student.subDistrict, student.state].filter(Boolean).join(", ") || "—" }}
                    <span v-if="student.pincode" class="font-mono text-slate-500"> ({{ student.pincode }})</span>
                  </p>
                  <p v-if="student.address" class="text-slate-500 dark:text-slate-400 mt-1 italic text-[11px]">
                    {{ student.address }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 2: Academic & Admission Record -->
          <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <GraduationCap class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span>Academic & Registry Information</span>
            </div>

            <div class="space-y-3 text-xs">
              <!-- Roll Number -->
              <div class="flex items-start gap-3">
                <Hash class="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Roll Number</p>
                  <p class="text-slate-800 dark:text-slate-200 font-mono font-bold mt-0.5">{{ student.rollNumber || "—" }}</p>
                </div>
              </div>

              <!-- Class -->
              <div class="flex items-start gap-3">
                <BookOpen class="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Class / Section</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    {{ typeof student.class === "object" ? student.class?.name : student.class || "—" }}
                  </p>
                </div>
              </div>

              <!-- Enrollment Date -->
              <div class="flex items-start gap-3">
                <Calendar class="w-4 h-4 text-cyan-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Admission Date</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    {{ student.enrollmentDate ? formatDate(student.enrollmentDate) : "—" }}
                  </p>
                </div>
              </div>

              <!-- Passout Details if any -->
              <div v-if="student.status === 'passout'" class="flex items-start gap-3">
                <CheckCircle class="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Passout Details</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    Year: {{ student.passoutYear || "—" }} • Session: {{ student.session || "—" }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Documents Section -->
        <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <FileText class="w-4 h-4 text-violet-600 dark:text-violet-400" />
            <span>Uploaded Identity & Academic Documents</span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- Document Card Reusable -->
            <div
              v-for="docItem in documentList"
              :key="docItem.key"
              class="p-4 rounded-xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#0F172A] flex flex-col justify-between space-y-3"
            >
              <div>
                <div class="flex items-center justify-between mb-2">
                  <span class="text-xs font-bold text-slate-800 dark:text-white">
                    {{ docItem.label }}
                  </span>
                  <span
                    v-if="student[docItem.key]"
                    class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full"
                  >
                    <CheckCircle class="w-3 h-3" /> Available
                  </span>
                  <span
                    v-else
                    class="text-[10px] text-slate-400 font-medium"
                  >
                    Not Uploaded
                  </span>
                </div>
              </div>

              <div v-if="student[docItem.key]" class="flex items-center gap-2 pt-2 border-t border-slate-200/60 dark:border-[#1E293B]">
                <button
                  type="button"
                  @click="$emit('view-document', student[docItem.key], docItem.label)"
                  class="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-semibold border border-indigo-200 dark:border-indigo-800 transition"
                >
                  <Eye class="w-3 h-3" />
                  <span>View</span>
                </button>
                <button
                  type="button"
                  @click="$emit('download-document', student[docItem.key], docItem.key)"
                  class="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-300 rounded-lg text-xs font-semibold border border-emerald-200 dark:border-emerald-800 transition"
                >
                  <Download class="w-3 h-3" />
                  <span>Get</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>

        <!-- SECTION 2: FEES & FINANCIAL LEDGER -->
        <div v-if="activeSection === 'fees'" class="space-y-6">
          <!-- Fee Loader -->
          <div v-if="isFeeLoading" class="p-12 text-center flex flex-col items-center justify-center">
            <AppLoader
              variant="card"
              size="md"
              message="Loading Financial Ledger..."
              subtext="Retrieving multi-class balance, monthly cycles, and verified receipts"
              color="indigo"
            />
          </div>

          <div v-else-if="!feeData" class="p-8 text-center text-slate-400 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449]">
            <Wallet class="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p class="text-xs font-semibold">No fee records found for this student.</p>
          </div>

          <div v-else class="space-y-6">
            <!-- 1. Outstanding Fee Summary Cards (Section 3 Requirement) -->
            <div class="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-5 rounded-2xl text-white shadow-md relative overflow-hidden">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                <div>
                  <span class="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">
                    Outstanding Fee Summary
                  </span>
                  <h3 class="text-base font-extrabold text-white">
                    {{ student.name }} (Roll: {{ student.rollNumber || "N/A" }})
                  </h3>
                </div>
                <span
                  class="px-3 py-1 rounded-full text-xs font-mono font-black self-start sm:self-auto"
                  :class="
                    (feeData.summary?.remainingBalance ?? feeData.lifetimeSummary?.grandTotalOutstanding ?? 0) === 0
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30'
                      : 'bg-rose-500/20 text-rose-300 border border-rose-400/30'
                  "
                >
                  {{ (feeData.summary?.remainingBalance ?? feeData.lifetimeSummary?.grandTotalOutstanding ?? 0) === 0 ? '✓ All Dues Cleared' : 'Dues Outstanding' }}
                </span>
              </div>

              <!-- 5 Hero Summary Cards -->
              <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                <!-- 1. Previous Class Outstanding -->
                <div class="bg-amber-500/15 border border-amber-400/30 backdrop-blur-xs p-3 rounded-xl flex flex-col justify-between">
                  <div class="flex items-center justify-between text-amber-300 mb-1">
                    <span class="text-[9px] font-bold uppercase tracking-wider">Previous Class Outstanding</span>
                    <History class="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  </div>
                  <span class="text-sm sm:text-base font-black font-mono text-amber-300">
                    ₹{{ (feeData.summary?.previousClassOutstanding ?? feeData.lifetimeSummary?.previousClassOutstanding ?? feeData.previousClassDues?.balance ?? 0).toLocaleString("en-IN") }}
                  </span>
                  <span class="text-[9px] text-amber-200/80 block mt-0.5 truncate">Carried-forward arrears</span>
                </div>

                <!-- 2. Current Class Fees -->
                <div class="bg-indigo-500/15 border border-indigo-400/30 backdrop-blur-xs p-3 rounded-xl flex flex-col justify-between">
                  <div class="flex items-center justify-between text-indigo-300 mb-1">
                    <span class="text-[9px] font-bold uppercase tracking-wider">Current Class Fees</span>
                    <GraduationCap class="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                  </div>
                  <span class="text-sm sm:text-base font-black font-mono text-indigo-200">
                    ₹{{ (feeData.summary?.currentClassFees ?? feeData.lifetimeSummary?.currentClassFees ?? 0).toLocaleString("en-IN") }}
                  </span>
                  <span class="text-[9px] text-indigo-300/80 block mt-0.5 truncate">Admission & tuition</span>
                </div>

                <!-- 3. Total Fees Due -->
                <div class="bg-white/10 border border-white/10 backdrop-blur-xs p-3 rounded-xl flex flex-col justify-between">
                  <div class="flex items-center justify-between text-slate-300 mb-1">
                    <span class="text-[9px] font-bold uppercase tracking-wider">Total Fees Due</span>
                    <Layers class="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  </div>
                  <span class="text-sm sm:text-base font-black font-mono text-white">
                    ₹{{ (feeData.summary?.totalFeesDue ?? feeData.lifetimeSummary?.totalFeesDue ?? feeData.lifetimeSummary?.grandTotalExpected ?? 0).toLocaleString("en-IN") }}
                  </span>
                  <span class="text-[9px] text-slate-300 block mt-0.5 truncate">Previous + Current</span>
                </div>

                <!-- 4. Total Amount Paid -->
                <div class="bg-emerald-500/15 border border-emerald-400/30 backdrop-blur-xs p-3 rounded-xl flex flex-col justify-between">
                  <div class="flex items-center justify-between text-emerald-300 mb-1">
                    <span class="text-[9px] font-bold uppercase tracking-wider">Total Amount Paid</span>
                    <CheckCircle class="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  </div>
                  <span class="text-sm sm:text-base font-black font-mono text-emerald-300">
                    ₹{{ (feeData.summary?.totalAmountPaid ?? feeData.lifetimeSummary?.totalAmountPaid ?? feeData.lifetimeSummary?.grandTotalPaid ?? 0).toLocaleString("en-IN") }}
                  </span>
                  <span class="text-[9px] text-emerald-300/80 block mt-0.5 truncate">{{ feeData.lifetimeSummary?.collectionRate || 100 }}% Realized</span>
                </div>

                <!-- 5. Remaining Balance -->
                <div class="col-span-2 sm:col-span-1 bg-rose-500/15 border border-rose-400/30 backdrop-blur-xs p-3 rounded-xl flex flex-col justify-between">
                  <div class="flex items-center justify-between text-rose-300 mb-1">
                    <span class="text-[9px] font-bold uppercase tracking-wider">Remaining Balance</span>
                    <AlertCircle class="w-3.5 h-3.5 text-rose-400 shrink-0" />
                  </div>
                  <span class="text-sm sm:text-base font-black font-mono text-rose-300">
                    ₹{{ (feeData.summary?.remainingBalance ?? feeData.lifetimeSummary?.remainingBalance ?? feeData.lifetimeSummary?.grandTotalOutstanding ?? 0).toLocaleString("en-IN") }}
                  </span>
                  <span class="text-[9px] text-rose-200/80 block mt-0.5 truncate">Grand Outstanding</span>
                </div>
              </div>
            </div>

            <!-- Carried-Forward Previous Class Dues Banner (When Arrears Exist) -->
            <div
              v-if="(feeData.summary?.previousClassOutstanding || feeData.previousClassDues?.hasPreviousDues) && (feeData.summary?.previousClassOutstanding || feeData.previousClassDues?.balance) > 0"
              class="bg-amber-50 dark:bg-amber-950/40 border-2 border-amber-400 dark:border-amber-600/60 p-4 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs"
            >
              <div class="flex items-start gap-3">
                <div class="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 flex items-center justify-center shrink-0">
                  <History class="w-5 h-5" />
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <h4 class="text-xs font-black uppercase tracking-wider text-amber-900 dark:text-amber-200">
                      Carried-Forward Previous Class Dues
                    </h4>
                    <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-800/80 text-amber-950 dark:text-amber-100">
                      Unpaid Arrears
                    </span>
                  </div>
                  <p class="text-xs text-amber-800 dark:text-amber-300 mt-1 leading-relaxed">
                    {{ feeData.previousClassDues?.message || 'Unpaid fees carried forward from previous class/session. Historical records remain separately identifiable and preserved.' }}
                  </p>
                </div>
              </div>
              <div class="text-right shrink-0 bg-white/70 dark:bg-black/30 px-3.5 py-2 rounded-xl border border-amber-300 dark:border-amber-700/60">
                <span class="text-[10px] text-amber-700 dark:text-amber-400 font-bold uppercase block">Previous Due Remaining</span>
                <span class="text-base font-black font-mono text-amber-900 dark:text-amber-200">
                  ₹{{ (feeData.summary?.previousClassOutstanding ?? feeData.previousClassDues?.balance ?? 0).toLocaleString('en-IN') }}
                </span>
              </div>
            </div>

            <!-- 2. Fee History Grouped by Academic Session and Class (Section 3 Requirement) -->
            <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
              <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-[#1E293B]">
                <div class="flex items-center gap-2">
                  <GraduationCap class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span class="font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider">
                    Fee History (Grouped by Class & Academic Session)
                  </span>
                </div>
                <span class="text-[11px] text-slate-400 font-medium">Historical & Current Breakdown</span>
              </div>

              <!-- Session Cards List -->
              <div class="space-y-4">
                <div
                  v-for="sItem in (feeData.sessionFeeHistory || computedFallbackSessionHistory)"
                  :key="sItem.session + '-' + sItem.className"
                  class="rounded-xl border overflow-hidden transition"
                  :class="
                    sItem.isCurrent
                      ? 'border-indigo-200 dark:border-indigo-900/60 bg-white dark:bg-[#111C30]'
                      : 'border-amber-200 dark:border-amber-900/50 bg-amber-50/20 dark:bg-amber-950/20'
                  "
                >
                  <!-- Session Header -->
                  <div
                    class="px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b"
                    :class="
                      sItem.isCurrent
                        ? 'bg-indigo-50/70 dark:bg-indigo-950/40 border-indigo-100 dark:border-indigo-900/40'
                        : 'bg-amber-100/60 dark:bg-amber-900/30 border-amber-200/70 dark:border-amber-800/40'
                    "
                  >
                    <div class="flex items-center gap-2.5">
                      <div
                        class="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                        :class="sItem.isCurrent ? 'bg-indigo-600 text-white' : 'bg-amber-600 text-white'"
                      >
                        <History v-if="!sItem.isCurrent" class="w-3.5 h-3.5" />
                        <GraduationCap v-else class="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <h4 class="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white">
                          {{ sItem.className }} – {{ sItem.isCurrent ? 'Current Academic Session' : 'Previous Academic Session' }}
                        </h4>
                        <span class="text-[10px] text-slate-500 dark:text-slate-400 font-mono">
                          Academic Session: {{ sItem.session }}
                        </span>
                      </div>
                    </div>

                    <div class="flex items-center gap-2 self-start sm:self-auto">
                      <span
                        v-if="!sItem.isCurrent"
                        class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-200 dark:bg-amber-800/80 text-amber-900 dark:text-amber-100"
                      >
                        Previous Class
                      </span>
                      <span
                        v-else
                        class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-200 dark:bg-indigo-800/80 text-indigo-900 dark:text-indigo-100"
                      >
                        Current Class
                      </span>
                      <span
                        class="px-2.5 py-0.5 rounded-full text-[11px] font-mono font-bold capitalize"
                        :class="
                          sItem.totalPending === 0
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                            : sItem.totalPaid > 0
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                            : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                        "
                      >
                        {{ sItem.totalPending === 0 ? 'Paid' : (sItem.totalPaid > 0 ? 'Partial' : 'Pending') }}
                      </span>
                    </div>
                  </div>

                  <!-- Session Items Table -->
                  <div class="overflow-x-auto">
                    <table class="w-full text-left text-xs min-w-[500px]">
                      <thead class="bg-slate-50 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[10px] border-b border-slate-200/70 dark:border-[#263449]">
                        <tr>
                          <th class="py-2.5 px-4">Fee Description</th>
                          <th class="py-2.5 px-3 text-right">Amount</th>
                          <th class="py-2.5 px-3 text-right">Paid</th>
                          <th class="py-2.5 px-3 text-right">Pending</th>
                          <th class="py-2.5 px-3 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
                        <tr
                          v-for="(itm, idx) in (sItem.items || [])"
                          :key="idx"
                          class="hover:bg-slate-50/60 dark:hover:bg-[#18243A]/40 transition"
                        >
                          <td class="py-2.5 px-4 font-bold text-slate-800 dark:text-[#F1F5F9]">
                            {{ itm.feeDescription }}
                          </td>
                          <td class="py-2.5 px-3 text-right font-mono font-semibold text-slate-700 dark:text-slate-300">
                            ₹{{ (itm.amount || 0).toLocaleString("en-IN") }}
                          </td>
                          <td class="py-2.5 px-3 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                            ₹{{ (itm.paid || 0).toLocaleString("en-IN") }}
                          </td>
                          <td class="py-2.5 px-3 text-right font-mono font-bold" :class="(itm.pending || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'">
                            ₹{{ (itm.pending || 0).toLocaleString("en-IN") }}
                          </td>
                          <td class="py-2.5 px-3 text-center">
                            <span
                              class="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase inline-block font-mono"
                              :class="
                                itm.status === 'Paid'
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                                  : itm.status === 'Partial'
                                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                                  : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                              "
                            >
                              {{ itm.status }}
                            </span>
                          </td>
                        </tr>
                        <!-- Subtotal Footer Row -->
                        <tr class="bg-slate-50 dark:bg-[#18243A] font-extrabold border-t-2 border-slate-200 dark:border-[#263449]">
                          <td class="py-2.5 px-4 text-slate-900 dark:text-white uppercase text-[11px]">
                            Total
                          </td>
                          <td class="py-2.5 px-3 text-right font-mono text-slate-900 dark:text-white">
                            ₹{{ (sItem.totalAmount || 0).toLocaleString("en-IN") }}
                          </td>
                          <td class="py-2.5 px-3 text-right font-mono text-emerald-600 dark:text-emerald-400">
                            ₹{{ (sItem.totalPaid || 0).toLocaleString("en-IN") }}
                          </td>
                          <td class="py-2.5 px-3 text-right font-mono" :class="(sItem.totalPending || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'">
                            ₹{{ (sItem.totalPending || 0).toLocaleString("en-IN") }}
                          </td>
                          <td class="py-2.5 px-3 text-center">
                            <span
                              class="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase font-mono"
                              :class="(sItem.totalPending || 0) === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'"
                            >
                              {{ (sItem.totalPending || 0) === 0 ? 'Paid' : 'Pending' }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Grand Outstanding Banner -->
              <div class="mt-4 p-4 rounded-xl bg-slate-900 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md">
                <div>
                  <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Overall Account Balance</span>
                  <div class="text-lg font-black text-white flex items-center gap-2">
                    <span>Grand Outstanding:</span>
                    <span class="text-rose-400 font-mono">₹{{ (feeData.summary?.remainingBalance ?? feeData.lifetimeSummary?.grandTotalOutstanding ?? 0).toLocaleString("en-IN") }}</span>
                  </div>
                </div>
                <div class="flex items-center gap-2 text-xs text-slate-300 font-mono">
                  <span class="bg-white/10 px-2.5 py-1 rounded-lg">Prev Arrears: ₹{{ (feeData.summary?.previousClassOutstanding ?? feeData.lifetimeSummary?.previousClassOutstanding ?? 0).toLocaleString("en-IN") }}</span>
                  <span class="text-slate-500">+</span>
                  <span class="bg-white/10 px-2.5 py-1 rounded-lg">Current Class: ₹{{ (feeData.summary?.currentClassFees ?? feeData.lifetimeSummary?.currentClassFees ?? 0).toLocaleString("en-IN") }}</span>
                </div>
              </div>
            </div>

            <!-- Promotion History Audit Timeline (Requirement 6 & 7) -->
            <div
              v-if="feeData.promotionHistory && feeData.promotionHistory.length > 0"
              class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-3"
            >
              <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-[#1E293B]">
                <div class="flex items-center gap-2">
                  <History class="w-4 h-4 text-indigo-500" />
                  <span class="font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider">
                    Student Promotion & Carry-Forward Audit History
                  </span>
                </div>
                <span class="text-[11px] text-slate-400 font-mono">
                  {{ feeData.promotionHistory.length }} Promotion Event{{ feeData.promotionHistory.length > 1 ? 's' : '' }}
                </span>
              </div>

              <div class="space-y-3">
                <div
                  v-for="prom in feeData.promotionHistory"
                  :key="prom._id"
                  class="p-3.5 rounded-xl border border-slate-200/70 dark:border-[#263449] bg-slate-50/50 dark:bg-[#18243A] space-y-2 text-xs"
                >
                  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                    <span class="font-bold text-slate-800 dark:text-white flex items-center gap-1.5">
                      <GraduationCap class="w-3.5 h-3.5 text-indigo-500" />
                      <span>Promoted: {{ prom.fromClassName || 'Class' }} ({{ prom.fromSession }}) &rarr; {{ prom.toClassName || 'Class' }} ({{ prom.toSession }})</span>
                    </span>
                    <span class="text-[10px] text-slate-400 font-mono">
                      {{ formatDate(prom.promotionDate) }} • By: {{ prom.promotedByName || 'Admin' }}
                    </span>
                  </div>
                  <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 border-t border-slate-200/50 dark:border-[#263449] font-mono text-[11px]">
                    <div>
                      <span class="text-slate-400 text-[10px] uppercase block">Carried Dues</span>
                      <strong class="text-amber-600 dark:text-amber-400">₹{{ (prom.financialSnapshot?.carriedForwardPreviousDues || 0).toLocaleString("en-IN") }}</strong>
                    </div>
                    <div>
                      <span class="text-slate-400 text-[10px] uppercase block">New Class Fees</span>
                      <strong class="text-indigo-600 dark:text-indigo-400">₹{{ (prom.financialSnapshot?.newClassFeesExpected || 0).toLocaleString("en-IN") }}</strong>
                    </div>
                    <div>
                      <span class="text-slate-400 text-[10px] uppercase block">Initial Outstanding</span>
                      <strong class="text-slate-900 dark:text-white">₹{{ (prom.financialSnapshot?.initialTotalOutstanding || 0).toLocaleString("en-IN") }}</strong>
                    </div>
                    <div>
                      <span class="text-slate-400 text-[10px] uppercase block">Status</span>
                      <span class="text-emerald-600 dark:text-emerald-400 font-bold uppercase text-[10px]">Verified & Locked</span>
                    </div>
                  </div>
                  <p v-if="prom.remarks" class="text-[11px] text-slate-500 italic">
                    Note: {{ prom.remarks }}
                  </p>
                </div>
              </div>
            </div>

            <!-- 3. Monthly Recurring Fee Obligations Table -->
            <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-3">
              <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-[#1E293B]">
                <div class="flex items-center gap-2">
                  <Calendar class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span class="font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider">
                    Monthly Fee Records Ledger
                  </span>
                </div>
                <span class="text-[11px] text-slate-400 font-mono">
                  Rate: ₹{{ (feeData.monthlySummary?.configuredMonthlyFee || 0).toLocaleString("en-IN") }}/mo
                </span>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full text-left text-xs min-w-[600px]">
                  <thead class="bg-slate-50 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[10px] border-b border-slate-200/80 dark:border-[#263449]">
                    <tr>
                      <th class="py-2.5 px-3">Billing Month</th>
                      <th class="py-2.5 px-3">Session</th>
                      <th class="py-2.5 px-3">Due Date</th>
                      <th class="py-2.5 px-3 text-right">Fee Due</th>
                      <th class="py-2.5 px-3 text-right">Paid</th>
                      <th class="py-2.5 px-3 text-right">Balance Left</th>
                      <th class="py-2.5 px-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
                    <tr
                      v-for="rec in (feeData.monthlyRecords || [])"
                      :key="rec._id || `${rec.year}-${rec.month}`"
                      class="hover:bg-slate-50/70 dark:hover:bg-[#18243A]/50 transition"
                    >
                      <td class="py-2.5 px-3 font-bold text-slate-800 dark:text-[#F1F5F9]">
                        {{ rec.monthName }} {{ rec.year }}
                      </td>
                      <td class="py-2.5 px-3 text-slate-400 font-mono text-[11px]">
                        {{ rec.academicSession || "—" }}
                      </td>
                      <td class="py-2.5 px-3 text-slate-500 font-mono text-[11px]">
                        {{ formatDateShort(rec.dueDate) }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono font-bold text-slate-800 dark:text-[#F1F5F9]">
                        ₹{{ (rec.feeDue || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        ₹{{ (rec.amountPaid || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono font-black" :class="(rec.balance || 0) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'">
                        ₹{{ (rec.balance || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-center">
                        <span
                          class="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase inline-block font-mono"
                          :class="
                            rec.status === 'paid'
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                              : rec.status === 'partial'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                              : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                          "
                        >
                          {{ rec.status }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="!feeData.monthlyRecords || feeData.monthlyRecords.length === 0">
                      <td colspan="7" class="py-6 text-center text-slate-400 text-xs">
                        No monthly fee records generated yet.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 4. Extra Item Debits & Credits (Adjustments) -->
            <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-3">
              <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-[#1E293B]">
                <div class="flex items-center gap-2">
                  <Receipt class="w-4 h-4 text-amber-500" />
                  <span class="font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider">
                    Item Debits & Credit Adjustments (ID Card, Badge, Concessions)
                  </span>
                </div>
                <div class="flex items-center gap-3 text-xs font-mono">
                  <span class="text-rose-600 font-bold">
                    Debits: ₹{{ (feeData.adjustmentSummary?.totalDebitAmount || 0).toLocaleString("en-IN") }}
                  </span>
                  <span class="text-emerald-600 font-bold">
                    Credits: ₹{{ (feeData.adjustmentSummary?.totalCreditAmount || 0).toLocaleString("en-IN") }}
                  </span>
                </div>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full text-left text-xs min-w-[500px]">
                  <thead class="bg-slate-50 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[10px] border-b border-slate-200/80 dark:border-[#263449]">
                    <tr>
                      <th class="py-2.5 px-3">Date</th>
                      <th class="py-2.5 px-3">Type</th>
                      <th class="py-2.5 px-3">Title / Item</th>
                      <th class="py-2.5 px-3 text-right">Amount</th>
                      <th class="py-2.5 px-3 text-center">Payment Status</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
                    <tr
                      v-for="adj in (feeData.adjustments || [])"
                      :key="adj._id"
                      class="hover:bg-slate-50/70 dark:hover:bg-[#18243A]/50 transition"
                    >
                      <td class="py-2.5 px-3 text-slate-500 font-mono text-[11px]">
                        {{ formatDateShort(adj.date || adj.createdAt) }}
                      </td>
                      <td class="py-2.5 px-3">
                        <span
                          class="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase inline-block font-mono"
                          :class="adj.type === 'debit' ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'"
                        >
                          {{ adj.type === 'debit' ? 'Debit Charge' : 'Credit Waiver' }}
                        </span>
                      </td>
                      <td class="py-2.5 px-3">
                        <span class="font-bold text-slate-800 dark:text-[#F1F5F9] block">{{ adj.title }}</span>
                        <span v-if="adj.remarks" class="text-[10px] text-slate-400 block">{{ adj.remarks }}</span>
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono font-bold" :class="adj.type === 'debit' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'">
                        {{ adj.type === 'debit' ? '+' : '-' }}₹{{ (adj.amount || 0).toLocaleString("en-IN") }}
                      </td>
                      <td class="py-2.5 px-3 text-center">
                        <span
                          class="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase inline-block font-mono"
                          :class="adj.paymentStatus === 'paid' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'"
                        >
                          {{ adj.paymentStatus }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="!feeData.adjustments || feeData.adjustments.length === 0">
                      <td colspan="5" class="py-6 text-center text-slate-400 text-xs">
                        No extra charges or credits recorded for this student.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 5. Payment Transactions Ledger -->
            <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-3">
              <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-[#1E293B]">
                <div class="flex items-center gap-2">
                  <Receipt class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span class="font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider">
                    Verified Payment Receipts & Ledger
                  </span>
                </div>
                <span class="text-[11px] text-slate-400 font-mono">
                  {{ (feeData.transactions || []).length }} Transactions
                </span>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full text-left text-xs min-w-[550px]">
                  <thead class="bg-slate-50 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[10px] border-b border-slate-200/80 dark:border-[#263449]">
                    <tr>
                      <th class="py-2.5 px-3">Receipt No</th>
                      <th class="py-2.5 px-3">Date</th>
                      <th class="py-2.5 px-3">Category</th>
                      <th class="py-2.5 px-3">Method</th>
                      <th class="py-2.5 px-3 text-right">Amount Received</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
                    <tr
                      v-for="tx in (feeData.transactions || [])"
                      :key="tx._id"
                      class="hover:bg-slate-50/70 dark:hover:bg-[#18243A]/50 transition"
                    >
                      <td class="py-2.5 px-3 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                        {{ tx.receiptNumber }}
                      </td>
                      <td class="py-2.5 px-3 text-slate-500 font-mono text-[11px]">
                        {{ formatDateShort(tx.paymentDate) }}
                      </td>
                      <td class="py-2.5 px-3 capitalize text-slate-700 dark:text-[#F1F5F9]">
                        {{ tx.feeCategory === "admission" ? "Admission Fee" : (tx.feeCategory === "adjustment" ? "Charge Item" : `${tx.monthName || 'Monthly'} ${tx.year || ''}`) }}
                      </td>
                      <td class="py-2.5 px-3 text-slate-600 dark:text-[#A6B4C8]">
                        {{ tx.paymentMethod }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-mono font-black text-emerald-600 dark:text-emerald-400">
                        ₹{{ (tx.amount || 0).toLocaleString("en-IN") }}
                      </td>
                    </tr>
                    <tr v-if="!feeData.transactions || feeData.transactions.length === 0">
                      <td colspan="5" class="py-6 text-center text-slate-400 text-xs">
                        No payments recorded yet.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- SECTION 3: ATTENDANCE ANALYTICS SECTION -->
        <div v-if="activeSection === 'attendance' && student?._id" class="pt-2">
          <StudentAttendanceAnalytics
            :selectedYear="selectedYear"
            :attendanceData="studentAttendanceData"
            @update:selectedYear="handleYearChange"
            @filter-change="handleFilterChange"
          />
        </div>
      </div>

      <!-- Footer -->
      <div
        class="p-4 sm:p-5 border-t border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#111C30] flex items-center justify-end gap-2.5 shrink-0"
      >
        <button
          @click="$emit('close')"
          class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        >
          Close
        </button>
        <button
          @click="$emit('edit', student)"
          class="inline-flex items-center justify-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition"
        >
          <Pencil class="w-3.5 h-3.5" />
          <span>Edit Student</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch } from "vue";
  import { useStore } from "vuex";
  import {
    X,
    User,
    GraduationCap,
    FileText,
    IdCard,
    CheckCircle,
    Eye,
    Download,
    Pencil,
    Mail,
    Phone,
    Calendar,
    MapPin,
    Hash,
    BookOpen,
    Wallet,
    Receipt,
    History,
    Layers,
    AlertCircle,
  } from "lucide-vue-next";
  import StudentAttendanceAnalytics from "@/components/Students/AttendanceGraph.vue";
  import { getStudentFeeDetails } from "@/services/feeService";

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    student: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["close", "edit", "promote", "view-document", "download-document"]);

  const store = useStore();

  const activeSection = ref("profile");
  const selectedYear = ref(new Date().getFullYear());
  const studentAttendanceData = ref(null);
  const feeData = ref(null);
  const isFeeLoading = ref(false);

  const formatDateShort = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fetchFeeDetails = async (id) => {
    if (!id) {
      feeData.value = null;
      return;
    }
    try {
      isFeeLoading.value = true;
      const res = await getStudentFeeDetails(id);
      if (res.data?.success) {
        feeData.value = res.data.data;
      }
    } catch (err) {
      console.error("fetchStudentFeeDetails error:", err);
      feeData.value = null;
    } finally {
      isFeeLoading.value = false;
    }
  };

  const documentList = [
    { key: "aadhaarCard", label: "Aadhaar Card" },
    { key: "birthCertificate", label: "Birth Certificate" },
    { key: "transferCertificate", label: "Transfer Certificate" },
    { key: "marksheet", label: "Marksheet" },
  ];

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "ST";

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  watch(
    () => props.student?._id,
    async (newId) => {
      if (!newId) {
        studentAttendanceData.value = null;
        return;
      }
      try {
        const data = await store.dispatch("fetchStudentYearlyAttendance", {
          userId: newId,
          year: selectedYear.value,
        });
        studentAttendanceData.value = data;
      } catch (err) {
        console.error("Failed to load attendance:", err);
        studentAttendanceData.value = null;
      }
      await fetchFeeDetails(newId);
    },
    { immediate: true },
  );

  const handleYearChange = async (year) => {
    selectedYear.value = year;
    if (!props.student?._id) return;
    try {
      const data = await store.dispatch("fetchStudentYearlyAttendance", {
        userId: props.student._id,
        year,
      });
      studentAttendanceData.value = data;
    } catch (err) {
      console.error("Failed to load attendance:", err);
      studentAttendanceData.value = null;
    }
  };

  const handleFilterChange = async ({ year, startDate, endDate }) => {
    if (!props.student?._id) return;
    selectedYear.value = year;
    try {
      const data = await store.dispatch("fetchStudentYearlyAttendance", {
        userId: props.student._id,
        year,
        startDate,
        endDate,
      });
      studentAttendanceData.value = data;
    } catch (err) {
      console.error("Failed to load filtered attendance:", err);
      studentAttendanceData.value = null;
    }
  };

  watch(
    () => props.show,
    (visible) => {
      if (!visible) {
        studentAttendanceData.value = null;
      }
    },
  );
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.97);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
</style>
